import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Search, Download } from "lucide-react";
import { getDiscountCodeById, deleteDiscountCode, getDiscountRedemptions, exportDiscountRedemptions } from "../../../services/discountService";
import Pagination from "../../Common/Pagination";

// No mock redemptions needed anymore

const ViewDiscountCodePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [code, setCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redemptions, setRedemptions] = useState([]);
  const [redemptionsLoading, setRedemptionsLoading] = useState(false);
  const [redemptionPage, setRedemptionPage] = useState(1);
  const [totalRedemptionsCount, setTotalRedemptionsCount] = useState(0);
  const [redemptionSearch, setRedemptionSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dateFilterSelect, setDateFilterSelect] = useState("All Dates");
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const REDEMPTIONS_PER_PAGE = 10;

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await getDiscountCodeById(id);
        setCode(data);
      } catch (err) {
        console.error("Failed to load discount:", err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  useEffect(() => {
    const fetchRedemptions = async () => {
      setRedemptionsLoading(true);
      try {
        const params = {
          page: redemptionPage,
          limit: REDEMPTIONS_PER_PAGE,
          search: redemptionSearch,
        };

        if (dateFilterSelect !== "All Dates") {
          params.dateFilter = dateFilterSelect;
        }

        if (dateFilterSelect === "Custom Range" && fromDate && toDate) {
          params.from = fromDate;
          params.to = toDate;
        }

        const data = await getDiscountRedemptions(id, params);
        setRedemptions(data.redemptions || []);
        setTotalRedemptionsCount(data.pagination?.total || 0);
      } catch (err) {
        console.error("Failed to load redemptions:", err);
      } finally {
        setRedemptionsLoading(false);
      }
    };

    if (id) {
      if (dateFilterSelect === "Custom Range" && (!fromDate || !toDate)) {
        return; // wait until both dates are selected
      }
      fetchRedemptions();
    }
  }, [id, redemptionPage, redemptionSearch, dateFilterSelect, fromDate, toDate]);

  const goBack = () => navigate("/admin-dashboard/discount-management");
  const goToEdit = () => navigate("/admin-dashboard/discount-management/create", { state: { editData: code } });

  const handleExportCSV = async () => {
    try {
      const params = {
        search: redemptionSearch,
      };

      if (dateFilterSelect !== "All Dates") {
        params.dateFilter = dateFilterSelect;
      }

      if (dateFilterSelect === "Custom Range" && fromDate && toDate) {
        params.from = fromDate;
        params.to = toDate;
      }

      await exportDiscountRedemptions(id, params);
    } catch (err) {
      console.error("Export failed:", err);
      alert("Failed to export redemptions. Please try again.");
    }
  };

  const handleDateFilterChange = (option) => {
    setDateFilterSelect(option);
    if (option === "Custom Range") {
      setIsCustomDateOpen(true);
    } else {
      setFromDate("");
      setToDate("");
      setRedemptionPage(1);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteDiscountCode(id);
      goBack();
    } catch (err) {
      console.error("Failed to delete discount code:", err);
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  // Derived stats from code
  const totalRedemptions = code?.totalUsage?.used ?? code?.redemptions ?? 0;
  const revenue = code?.revenue ?? 0;
  const discountGranted = code?.discountGranted ?? Math.round(revenue * 0.43);
  const avgDiscount =
    totalRedemptions > 0 ? (discountGranted / totalRedemptions).toFixed(2) : "0";
  const used = code?.totalUsage?.used ?? 0;
  const limit = code?.totalUsage?.limit ?? code?.globalCap ?? null;
  const left = limit !== null ? limit - used : null;

  // Expiry countdown calculation
  const getExpiryCountdown = () => {
    if (code?.isDeleted || code?.status === "deleted") return "Deleted";
    if (code?.status === "expired" || code?.status === "fully_redeemed" || code?.status === "disabled") {
      return code.status === "expired" ? "Expired" : code.status === "fully_redeemed" ? "Fully Redeemed" : "Disabled";
    }

    const endDateStr = code?.endAt || code?.expiryDate;
    if (!endDateStr) {
      return "No Expiry";
    }

    const end = new Date(endDateStr);
    const now = new Date();
    const diff = end - now;

    if (diff <= 0) {
      return "Expired";
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);

    if (days > 0) {
      return `${days}d ${hours}h remaining`;
    }

    if (hours > 0) {
      const mins = Math.floor((diff / 1000 / 60) % 60);
      return `${hours}h ${mins}m remaining`;
    }

    const mins = Math.floor((diff / 1000 / 60) % 60);
    return `${mins}m remaining`;
  };

  const expiryCountdown = getExpiryCountdown();

  if (loading) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-secondary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!code) {
    return (
      <div className="min-h-screen p-6">
        <button onClick={goBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 py-4 text-sm font-medium">
          <ArrowLeft className="w-4 h-4" />
          Back To Code List
        </button>
        <p className="text-gray-500 mt-4">Discount code not found.</p>
      </div>
    );
  }

  const getStatusDisplay = () => {
    if (code.isDeleted || code.status === "deleted") {
      return { label: "Deleted", class: "bg-red-100 text-red-700" };
    }
    if (code.status === "disabled") {
      return { label: "Disabled", class: "bg-red-100 text-red-700" };
    }
    if (code.status === "fully_redeemed") {
      return { label: "Fully Redeemed", class: "bg-[#FFF3C2] text-[#A68604]" };
    }
    if (code.status === "expired") {
      return { label: "Expired", class: "bg-[#F3D7DE] text-[#EB0000]" };
    }

    const now = new Date();
    const start = code.startAt || code.startDate;
    const end = code.endAt || code.expiryDate;

    if (end && new Date(end) < now) {
      return { label: "Expired", class: "bg-[#F3D7DE] text-[#EB0000]" };
    }

    if (start && new Date(start) > now) {
      return { label: "Scheduled", class: "bg-blue-100 text-blue-700" };
    }

    return { label: "Active", class: "bg-[#E7F6EC] text-[#0F973D]" };
  };

  const status = getStatusDisplay();
  const totalRedemptionPages = Math.ceil(totalRedemptionsCount / REDEMPTIONS_PER_PAGE);

  return (
    <div className="min-h-screen p-3 sm:p-4 md:p-6 space-y-6">
      {/* Back */}
      <button
        onClick={goBack}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 py-4 text-sm font-medium"
      >
        <ArrowLeft className="w-4 h-4" />
        Back To Code List
      </button>

      {/* Header Card */}
      <div className="bg-white rounded-2xl border border-[#E0E4E2] p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h1 className="text-xl font-bold text-gray-900">{code.name}</h1>
              <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-2xl ${status.class}`}>
                {status.label}
              </span>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-700">
                {code.code}
              </span>
            </div>
            <p className="text-xs text-gray-500">
              Created February 20th, 2026 by Admin
            </p>
          </div>
          <div className="flex gap-2">
            {!code.isDeleted && code.status !== 'deleted' && (
              <button
                onClick={goToEdit}
                className="px-4 py-2.5 text-sm font-medium text-white bg-secondary rounded-2xl hover:bg-secondary/90"
              >
                Edit Code
              </button>
            )}
            {!code.isDeleted && code.status !== 'deleted' && (
              <button
                onClick={() => setIsDeleteModalOpen(true)}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 border border-[#E0E4E2] rounded-2xl hover:bg-gray-50 text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Delete Code
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-[#E0E4E2] p-6">
          <p className="text-sm font-medium text-gray-500 mb-1">Total Redemptions</p>
          <p className="text-2xl font-bold text-gray-900">{totalRedemptions}</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E0E4E2] p-6">
          <p className="text-sm font-medium text-gray-500 mb-1">Revenue Generated</p>
          <p className="text-2xl font-bold text-gray-900">${revenue.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E0E4E2] p-6">
          <p className="text-sm font-medium text-gray-500 mb-1">Discount Granted</p>
          <p className="text-2xl font-bold text-gray-900">${discountGranted.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E0E4E2] p-6">
          <p className="text-sm font-medium text-gray-500 mb-1">Avg Discount</p>
          <p className="text-2xl font-bold text-gray-900">${avgDiscount}</p>
        </div>
      </div>

      {/* Usage & Expiry Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-[#E0E4E2] p-6">
          <p className="text-sm font-semibold text-gray-900 mb-3">Usage</p>
          {limit === null ? (
            <div className="mb-2">
              <span className="text-sm font-medium text-gray-900">
                {used} / Unlimited
              </span>
            </div>
          ) : (
            <>
              <div className="flex justify-between text-sm mb-2">
                <span className="font-medium text-gray-900">
                  {used}/{limit}
                </span>
                <span className="text-gray-500">{left} left</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${(used / limit) * 100 > 90
                    ? "bg-[#EB0000]"
                    : (used / limit) * 100 >= 70
                      ? "bg-[#F5C956]"
                      : "bg-[#0F973D]"
                    }`}
                  style={{
                    width: `${limit > 0 ? Math.min(100, (used / limit) * 100) : 0}%`,
                  }}
                />
              </div>
            </>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-[#E0E4E2] p-6">
          <p className="text-sm font-semibold text-gray-900 mb-1">Expiry Countdown</p>
          <p className="text-xl font-bold text-gray-900">{expiryCountdown}</p>
        </div>
      </div>

      {/* Configuration Card */}
      <div className="bg-white rounded-2xl border border-[#E0E4E2] p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm ">
          <div>
            <p className="text-gray-500 mb-1">Eligibility</p>
            <p className="font-medium text-gray-900">{code.eligibilityLabel || code.eligibility}</p>
          </div>
          <div>
            <p className="text-gray-500 mb-1">Discount</p>
            <p className="font-medium text-gray-900">{code.discountLabel || (code.discount + (code.discountType === 'percentage' ? '%' : ''))} off</p>
          </div>
          <div>
            <p className="text-gray-500 mb-1">Applicable Plans</p>
            <div className="flex flex-wrap gap-1">
              {Array.isArray(code.selectedPlans) && code.selectedPlans.length > 0 ? (
                code.selectedPlans.map((p, idx) => (
                  <span key={idx} className="inline-flex px-2 py-1 text-xs font-medium rounded-2xl bg-gray-100 text-gray-700">
                    {typeof p === 'object' ? (p.name || p.id) : p}
                  </span>
                ))
              ) : (
                <span className="text-gray-400">No plans specified</span>
              )}
            </div>
          </div>
          <div>
            <p className="text-gray-500 mb-1">Start Date And Time</p>
            <p className="font-medium text-gray-900">
              {code.startAt ? new Date(code.startAt).toLocaleString() : "Immediately"}
            </p>
          </div>
          <div>
            <p className="text-gray-500 mb-1">Expiry Date And Time</p>
            <p className="font-medium text-gray-900">
              {code.endAt ? new Date(code.endAt).toLocaleString() : "No Expiry"}
            </p>
          </div>
          <div>
            <p className="text-gray-500 mb-1">Global Usage Limit</p>
            <p className="font-medium text-gray-900">{code.totalUsage?.limit || code.globalCap || "Unlimited"}</p>
          </div>
          <div>
            <p className="text-gray-500 mb-1">Per-User Limit</p>
            <p className="font-medium text-gray-900">{code.perUserLimit || "No limit"}</p>
          </div>
          <div>
            <p className="text-gray-500 mb-1">Last Modified</p>
            <p className="font-medium text-gray-900">
              {code.updatedAt ? new Date(code.updatedAt).toLocaleString() : "Unknown"}
            </p>
          </div>
        </div>
      </div>

      {/* Redemption Log */}
      <div className="bg-white rounded-2xl border border-[#E0E4E2] overflow-hidden">
        <div className="p-6 border-b border-[#E0E4E2]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h3 className="text-lg font-semibold text-gray-900">Redemption Log</h3>
            <button
              onClick={handleExportCSV}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary text-white text-sm font-medium rounded-2xl hover:bg-secondary/90 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>
        <div className="p-6 border-b border-[#E0E4E2]">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by user"
                value={redemptionSearch}
                onChange={(e) => {
                  setRedemptionSearch(e.target.value);
                  setRedemptionPage(1);
                }}
                className="w-full martial-input pl-10 pr-4 py-2.5 bg-gray-50 border border-[#E0E4E2] rounded-2xl text-sm text-gray-900 placeholder-gray-500"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              {(dateFilterSelect === "Custom Range" && (fromDate || toDate)) && (
                <div className="flex items-center gap-2 px-3 py-2 bg-[#F6F8F7] border border-[#E0E4E2] rounded-xl text-sm text-gray-700">
                  <span className="font-medium">{fromDate || "Start"}</span>
                  <span className="text-gray-400">→</span>
                  <span className="font-medium">{toDate || "End"}</span>
                  <button
                    onClick={() => {
                      setFromDate("");
                      setToDate("");
                      setDateFilterSelect("All Dates");
                      setRedemptionPage(1);
                    }}
                    className="ml-2 text-gray-400 hover:text-gray-600 font-bold"
                  >
                    ×
                  </button>
                </div>
              )}
              <select
                value={dateFilterSelect}
                onChange={(e) => handleDateFilterChange(e.target.value)}
                className="w-full sm:w-48 px-4 py-2.5 bg-gray-50 border border-[#E0E4E2] rounded-2xl text-sm text-gray-900 hover:bg-gray-100 transition-colors cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23131313%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')] bg-[length:10px] bg-no-repeat bg-[position:right_1rem_center]"
              >
                <option value="All Dates">All Dates</option>
                <option value="Today">Today</option>
                <option value="Last 7 Days">Last 7 Days</option>
                <option value="Last 30 Days">Last 30 Days</option>
                <option value="This Month">This Month</option>
                <option value="Last Month">Last Month</option>
                <option value="Custom Range">Custom Range</option>
              </select>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto relative min-h-[200px]">
          {redemptionsLoading && (
            <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center">
              <div className="animate-spin w-6 h-6 border-2 border-secondary border-t-transparent rounded-full" />
            </div>
          )}
          <p className="px-6 py-2 text-sm text-gray-500">
            Showing {redemptions.length} of {totalRedemptionsCount} redemptions
          </p>
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                  User ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                  User Email
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                  Plan Purchase
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                  Subscription ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                  Invoice ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                  Original
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                  Discount
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                  Charged
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {redemptions.length > 0 ? (
                redemptions.map((r) => (
                  <tr key={r.id || r._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">{r.userId?.id || r.userId || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{r.userEmail || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {r.planPurchase || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 font-mono whitespace-nowrap">
                      {r.subscriptionId || r.stripeSubscriptionId || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 font-mono whitespace-nowrap">
                      {r.invoiceId || r.stripeInvoiceId || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {r.originalPrice != null
                        ? `${r.currency ? r.currency.toUpperCase() + ' ' : '$'}${Number(r.originalPrice).toFixed(2)}`
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {r.discountCharged != null
                        ? `${r.currency ? r.currency.toUpperCase() + ' ' : '$'}${Number(r.discountCharged).toFixed(2)}`
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {r.amountAfterDiscount != null
                        ? `${r.currency ? r.currency.toUpperCase() + ' ' : '$'}${Number(r.amountAfterDiscount).toFixed(2)}`
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {new Date(r.date || r.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-sm text-gray-500">
                    No redemptions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {totalRedemptionPages > 1 && (
          <div className="p-6">
            <Pagination
              currentPage={redemptionPage}
              totalPages={totalRedemptionPages}
              onPageChange={setRedemptionPage}
              hasNextPage={redemptionPage < totalRedemptionPages}
              hasPrevPage={redemptionPage > 1}
              totalItems={totalRedemptionsCount}
              itemsPerPage={REDEMPTIONS_PER_PAGE}
            />
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-[400px] p-8 text-center shadow-xl">
            <h3 className="text-[22px] font-bold text-gray-900 mb-3">Delete Discount Code?</h3>
            <p className="text-[14px] text-gray-500 mb-8 leading-relaxed px-2">
              This code will stop working for all future checkouts immediately.
              Active sessions will be revalidated at payment time.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-[#cc0000] rounded-xl hover:bg-[#aa0000] transition-colors"
              >
                Delete Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Date Range Modal */}
      {isCustomDateOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-[500px] p-6 sm:p-8 shadow-xl relative">
            <button
              onClick={() => {
                setIsCustomDateOpen(false);
                if (!fromDate || !toDate) {
                  setDateFilterSelect("All Dates");
                  setFromDate("");
                  setToDate("");
                }
              }}
              className="absolute top-4 right-4 w-6 h-6 flex items-center justify-center bg-black text-white rounded-full hover:bg-black/80 transition-colors"
            >
              <span className="text-xl leading-none font-medium pb-0.5">×</span>
            </button>
            <h3 className="text-[28px] font-bold text-[#111827] mb-8 mt-2 font-space-grotesk text-left">Custom Range</h3>

            <div className="space-y-6 mb-8 mt-2">
              <div>
                <label className="block text-sm font-medium text-[#8B7D7D] mb-2 font-space-grotesk">Start Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-[#E0E4E2] rounded-xl text-[15px] text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#206954]/20 focus:border-[#206954] transition-all font-medium"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#8B7D7D] mb-2 font-space-grotesk">End Date</label>
                <input
                  type="date"
                  value={toDate}
                  min={fromDate} /* Ensure end date is not before start date */
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-[#E0E4E2] rounded-xl text-[15px] text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#206954]/20 focus:border-[#206954] transition-all font-medium"
                />
              </div>
            </div>
            <div className="flex justify-center mt-10">
              <button
                onClick={() => {
                  setIsCustomDateOpen(false);
                  setRedemptionPage(1);
                }}
                className="w-full px-4 py-4 text-base font-bold text-white bg-[#206954] rounded-2xl hover:bg-[#1b5846] transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewDiscountCodePage;
